import { useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { themeVars } from "@/lib/theme";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Upload, ImageIcon } from "lucide-react";
import { toast } from "sonner";

export const SECTORS = [
  { code: "BAR", label: "Résidentiel" }, { code: "BAT", label: "Tertiaire" }, { code: "IND", label: "Industriel" },
  { code: "AGRI", label: "Agricole" }, { code: "RES", label: "Réseau de chaleur" },
];
const ACCEPT = ".png,.jpg,.jpeg,.svg,.webp";
const HEX = /^#[0-9a-fA-F]{6}$/;
const slugify = (v) => v.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);

const ColorField = ({ label, value, onChange, testid }) => (
  <div className="space-y-1.5">
    <Label>{label}</Label>
    <div className="flex items-center gap-2">
      <input type="color" value={HEX.test(value) ? value : "#000000"} onChange={(e) => onChange(e.target.value.toUpperCase())} className="h-9 w-12 rounded-md border border-border cursor-pointer bg-transparent" data-testid={`${testid}-picker`} />
      <Input value={value} onChange={(e) => onChange(e.target.value)} className="metric uppercase" maxLength={7} data-testid={`${testid}-input`} />
    </div>
  </div>
);

const Preview = ({ name, logo, primary, accent }) => {
  const ok = HEX.test(primary) && HEX.test(accent);
  const vars = ok ? themeVars(primary, accent) : {};
  return (
    <div style={vars} className="force-light rounded-xl border border-border overflow-hidden" data-testid="partner-preview">
      <div className="flex items-center gap-3 px-4 h-14 bg-white border-b border-border">
        {logo ? <img src={logo} alt="" className="h-10 w-auto object-contain" /> : <ImageIcon className="h-6 w-6 text-muted-foreground" />}
        <span className="text-sm font-heading font-bold text-genis-deep">{name || "Nom du partenaire"}</span>
        <span className="ml-auto rounded-lg bg-genis/10 text-genis-dark text-xs font-medium px-3 py-1.5">Nouvelle simulation</span>
      </div>
      <div className="gradient-genis text-white px-4 py-3 flex items-center justify-between">
        <span className="text-sm font-semibold">Montant total · 12 450 €</span>
        <span className="relative grid place-items-center h-9 w-9">
          <span className="absolute right-0 top-0 h-6 w-6 rounded-full bg-genis-accent/70" />
          <span className="relative text-xs font-bold">CEE</span>
        </span>
      </div>
      <div className="p-4 bg-white flex items-center gap-3">
        <span className="rounded-md bg-genis text-white text-xs font-semibold px-3 py-2">Bouton principal</span>
        <span className="rounded-full bg-genis-accent/25 text-genis-deep text-xs font-semibold px-3 py-1">Pastille accent</span>
      </div>
    </div>
  );
};

export const PartnerDialog = ({ partner, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: partner?.name || "", slug: partner?.slug || "", allowed_sectors: partner?.allowed_sectors || ["BAR", "BAT"],
    logo_url: partner?.logo_url || "", primary_color: partner?.primary_color || "#33ADF5", accent_color: partner?.accent_color || "#A5CA00",
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error("Logo trop volumineux (2 Mo maximum)");
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/partners/logo", fd);
      set("logo_url", data.logo_url);
      toast.success("Logo envoyé");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.allowed_sectors.length) return toast.error("Au moins un secteur est requis");
    if (!HEX.test(form.primary_color) || !HEX.test(form.accent_color)) return toast.error("Couleurs au format #RRGGBB");
    setSaving(true);
    try {
      if (partner) await api.put(`/admin/partners/${partner.id}`, form);
      else await api.post("/admin/partners", form);
      toast.success(partner ? "Partenaire mis à jour" : "Partenaire créé");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggle = (c, v) => set("allowed_sectors", v ? [...form.allowed_sectors, c] : form.allowed_sectors.filter((x) => x !== c));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="partner-dialog">
        <DialogHeader><DialogTitle>{partner ? `Modifier ${partner.name}` : "Nouveau partenaire"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Nom *</Label>
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value, slug: partner ? f.slug : slugify(e.target.value) }))} data-testid="partner-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Identifiant *</Label>
            <Input value={form.slug} onChange={(e) => set("slug", slugify(e.target.value))} data-testid="partner-slug-input" />
          </div>
          <div className="sm:col-span-2 space-y-1.5">
            <Label>Logo (PNG, JPG, SVG, WEBP · 2 Mo max)</Label>
            <div className="flex items-center gap-3">
              <div className="h-16 w-28 rounded-lg border border-dashed border-border grid place-items-center bg-white overflow-hidden">
                {form.logo_url ? <img src={form.logo_url} alt="logo" className="max-h-14 max-w-[100px] object-contain" data-testid="partner-logo-preview" /> : <ImageIcon className="h-6 w-6 text-muted-foreground" />}
              </div>
              <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid="partner-logo-file" />
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="partner-logo-upload-btn">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-1.5" /> Choisir un logo</>}
              </Button>
            </div>
          </div>
          <ColorField label="Couleur principale" value={form.primary_color} onChange={(v) => set("primary_color", v)} testid="partner-primary" />
          <ColorField label="Couleur d'accent" value={form.accent_color} onChange={(v) => set("accent_color", v)} testid="partner-accent" />
          <p className="sm:col-span-2 -mt-2 text-xs text-muted-foreground">Principale : boutons, bandeaux, titres · Accent : pastilles, surlignage.</p>
          <div className="sm:col-span-2">
            <Label className="mb-2 block">Secteurs autorisés *</Label>
            <div className="flex flex-wrap gap-4">
              {SECTORS.map((s) => (
                <label key={s.code} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={form.allowed_sectors.includes(s.code)} onCheckedChange={(v) => toggle(s.code, !!v)} data-testid={`partner-sector-${s.code}`} />
                  {s.label}
                </label>
              ))}
            </div>
          </div>
          <div className="sm:col-span-2">
            <Label className="mb-2 block">Aperçu en direct</Label>
            <Preview name={form.name} logo={form.logo_url} primary={form.primary_color} accent={form.accent_color} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button onClick={save} disabled={saving || uploading || !form.name || !form.slug} className="bg-genis hover:bg-genis-dark text-white" data-testid="partner-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
